import {
  Button,
  Card,
  Divider,
  Flex,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Switch,
  Typography,
} from "antd";
import { Eye, HelpCircle, Pencil, Trash2 } from "lucide-react";
import React, { Suspense, useCallback, useState } from "react";
import { Question } from "@types";
import { useTranslation } from "react-i18next";
import { useDeleteQuestion, useEditQuestion } from "@hooks";
import { BankPoolForm } from "@components";
import { useExamStore } from "@store";
import { ExamContent } from "../question-preview/ExamContent";

const Paragraph = Typography;

interface Props {
  question: Question;
}
const BankPoolListItem = React.memo(({ question }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const setClickedQuestion = useExamStore((state) => state.setClickedQuestion);
  const { t } = useTranslation();
  const questionDelete = useDeleteQuestion();
  const editQuestion = useEditQuestion();

  const [isCheck, setIsCheck] = useState(question.state === 1);

  const onChange = useCallback((checked: boolean) => {
    setIsCheck(checked);
    editQuestion.mutate(
      { ...question, state: checked ? 1 : 2 },
      { onError: () => setIsCheck(!checked) }
    );
  }, [editQuestion, question]);

  return (
    <div style={{ width: "100%", padding: "8px 4px" }}>
      <Card
        bordered={false}
        style={{ boxShadow: "rgba(0, 0, 0, 0.16) 1px 0px 10px" }}
      >
        <Flex justify="space-between" align="top" gap={'middle'}>
          <Typography.Paragraph ellipsis={{ rows: 1, expandable: false } }>
            {new DOMParser().parseFromString(question.text, 'text/html').body.textContent}
          </Typography.Paragraph>
          <Space>
            <Button
              icon={<Eye />}
              onClick={() => {
                setClickedQuestion(question);
                setIsPreviewOpen(true);
              }}
            />
            <Button id={`edit-${question.id}`} icon={<Pencil />} onClick={() => setIsModalOpen(true)} />
            {/* <Button icon={<Calendar />} /> */}
            <Popconfirm
              title={t("pages.bankPool.deleteQuestion")}
              description={t("pages.bankPool.deleteQuestionDesc")}
              onConfirm={() => questionDelete.mutate(question.id)}
              okButtonProps={{ loading: questionDelete.isPending }}
              icon={<HelpCircle style={{ color: "red", marginRight: 8 }} />}
            >
              <Button id={`delete-${question.id}`} danger icon={<Trash2 />} />
            </Popconfirm>
          </Space>
        </Flex>
        <Flex justify="space-between" align="flex-end">
          <Space.Compact direction="vertical">
            <Paragraph>
              <Typography.Text strong>
                {t("pages.bankPool.authorFilter")}:{" "}
              </Typography.Text>
              <Typography.Text>{`${question.user?.firstName} ${question.user?.lastName}`}</Typography.Text>
            </Paragraph>
            <Paragraph>
              <Typography.Text strong>
                {t("pages.bankPool.categoryFilter")}:{" "}
              </Typography.Text>
              <Space split={<Divider type="vertical" style={{ margin: 0 }} />}>
                {question.categories.map((category) => (
                  <Typography.Link key={category.id}>
                    {category.name}
                  </Typography.Link>
                ))}
              </Space>
            </Paragraph>
          </Space.Compact>
          <Space>
            <Typography.Text strong>
              {t("pages.bankPool.statusFilter")}:{" "}
            </Typography.Text>
            <Switch
              loading={editQuestion.isPending}
              value={isCheck}
              onChange={onChange}
              size="small"
            />
          </Space>
        </Flex>

        <Modal
          title={t("pages.bankPool.editQuestion")}
          footer={null}
          width={600}
          onCancel={() => setIsModalOpen(false)}
          destroyOnClose
          styles={{ body: {
            paddingRight: 8,
            paddingLeft: 4,
            height: "calc(80vh - 30px)",
            overflowY: "auto",
            overflowX: "hidden",
          } }}
          open={isModalOpen}
        >
          <Suspense fallback={<Spin/>}>
          <BankPoolForm
            setIsModalOpen={setIsModalOpen}
            examQuestionType={question.questionType.id}
            question={question}
          />
          </Suspense>
        </Modal>
        <Modal
          title={t("pages.bankPool.preview")}
          footer={null}
          onCancel={() => setIsPreviewOpen(false)}
          destroyOnClose
          width={1000}
          open={isPreviewOpen}
        >
          <ExamContent />
        </Modal>
      </Card>
    </div>
  );
});

export default BankPoolListItem;
